// src/components/TermsConditionsList.tsx
import React, { useState } from 'react';
import { Edit2, Trash2, FileText, Eye } from 'lucide-react';
import { TermsConditions } from '../types/termscondition';

interface TermsConditionsListProps {
  termsList: TermsConditions[];
  onEdit: (item: TermsConditions) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
}

export function TermsConditionsList({ termsList, onEdit, onDelete, loading }: TermsConditionsListProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const getLetterTypeColor = (letterType: string) => {
    switch (letterType?.toLowerCase()) {
      case 'offer':
        return 'bg-green-100 text-green-700';
      case 'joining':
        return 'bg-blue-100 text-blue-700';
      case 'salary':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getLetterTypeLabel = (letterType: string) => {
    switch (letterType?.toLowerCase()) {
      case 'offer':
        return 'Offer Letter';
      case 'joining':
        return 'Joining Letter';
      case 'salary':
        return 'Salary Letter';
      default:
        return letterType;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!termsList || termsList.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No Terms & Conditions found. Click "Add New" to create one.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {termsList.map((tc) => (
        <div key={tc.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
          <div className="p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="text-lg font-semibold text-gray-900">{tc.title}</h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getLetterTypeColor(tc.letter_type)}`}>
                    {getLetterTypeLabel(tc.letter_type)}
                  </span>
                </div>
                
                {tc.created_at && (
                  <p className="text-xs text-gray-400 mb-2">
                    Created: {new Date(tc.created_at).toLocaleDateString()}
                  </p>
                )}

                {/* Preview of content */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {tc.content}
                  </p>
                </div>

                {/* Expand/Collapse for full content */}
                {expandedId === tc.id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-sm text-gray-700">
                        {tc.content}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => setExpandedId(expandedId === tc.id ? null : tc.id!)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title={expandedId === tc.id ? "Show less" : "Read more"}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onEdit(tc)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => tc.id && onDelete(tc.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}